<template>
  <LayupSpinner v-show="isLoading" :size="10" />
  <div v-show="!isLoading">
    <div>
      <h1 class="font-semibold text-xl text-grey-light-900">Orders</h1>
      <p class="text-grey-light-600 text-sm">
        Manage boost rewards and lay-by plans as well as payment options for your customers.
      </p>
    </div>
    <SectionDivider class="my-4" />
    <!-- Payment Methods -->
    <div class="grid lg:grid-cols-5 gap-4 md:grid-cols-5 sm:grid-cols-1">
      <div class="col-span-2">
        <h5 class="font-semibold text-grey-light-900">Payment Methods</h5>
        <p class="text-grey-light-600 text-sm">Ways in which customers can pay.</p>
      </div>
      <div class="col-span-2">
        <FormField v-slot="{ errors }" name="paymentMethods">
          <FormItem>
            <div class="flex flex-col gap-4">
              <FormField
                v-if="props.settings.features?.enableCapitecPay"
                v-slot="{ value, handleChange }"
                type="checkbox"
                name="paymentMethods.capitecPay"
              >
                <FormItem>
                  <div class="flex gap-2 items-center">
                    <FormControl>
                      <CheckboxComponent
                        :model-value="value"
                        @update:model-value="handleChange"
                        :disabled="!shouldAllowMerchantToEnablePaymentMethods.capitecPay"
                      />
                    </FormControl>
                    <FormLabel class="font-medium">Capitec Pay</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormField
                v-if="props.settings.features?.enableCreditCard"
                v-slot="{ value, handleChange }"
                type="checkbox"
                name="paymentMethods.card"
              >
                <FormItem>
                  <div class="flex gap-2 items-center">
                    <FormControl>
                      <CheckboxComponent
                        :model-value="value"
                        @update:model-value="handleChange"
                        :disabled="!shouldAllowMerchantToEnablePaymentMethods.creditCard"
                      />
                    </FormControl>
                    <FormLabel class="font-medium">Credit/Debit Card</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormField
                v-if="props.settings.features?.enableInstantEFT"
                v-slot="{ value, handleChange }"
                type="checkbox"
                name="paymentMethods.instantEft"
              >
                <FormItem>
                  <div class="flex gap-2 items-center">
                    <FormControl>
                      <CheckboxComponent
                        :model-value="value"
                        @update:model-value="handleChange"
                        :disabled="!shouldAllowMerchantToEnablePaymentMethods.eft"
                      />
                    </FormControl>
                    <FormLabel class="font-medium">Instant EFT</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              </FormField>
              <div class="flex flex-col gap-2">
                <FormField
                  v-if="props.settings.features?.enableDebiCheck"
                  v-slot="{ value, handleChange }"
                  type="checkbox"
                  name="paymentMethods.debiCheck"
                >
                  <FormItem>
                    <div class="flex gap-2 items-center">
                      <FormControl>
                        <CheckboxComponent
                          :model-value="value"
                          @update:model-value="handleChange"
                          :disabled="!shouldAllowMerchantToEnablePaymentMethods.debicheck"
                        />
                      </FormControl>
                      <FormLabel class="font-medium">DebiCheck</FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                </FormField>
                <div
                  v-if="
                    props.settings.features?.enableDebiCheck &&
                    isDebiCheckSelected &&
                    shouldAllowMerchantToEnablePaymentMethods.debicheck
                  "
                  class="my-2 space-y-2"
                  :class="{ hidden: !isDebiCheckSelected }"
                >
                  <div class="flex gap-2 items-center">
                    <SwitchComponent
                      :model-value="debiCheckMinAmountEnabled"
                      @update:model-value="debiCheckMinAmountEnabled = !debiCheckMinAmountEnabled"
                      :disabled="!isDebiCheckSelected"
                    />
                    <LabelComponent>DebiCheck minimum amount</LabelComponent>
                  </div>
                  <div v-show="debiCheckMinAmountEnabled">
                    <FormField v-slot="{ componentField }" name="paymentMethods.debiCheckMinAmount">
                      <FormItem v-show="debiCheckMinAmountEnabled">
                        <FormControl>
                          <Input
                            type="text"
                            placeholder="DebiCheck minimum amount"
                            :disabled="!debiCheckMinAmountEnabled"
                            v-bind="componentField"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    </FormField>
                  </div>
                </div>
              </div>
              <FormField
                v-if="props.settings.features?.enablePayAt"
                v-slot="{ value, handleChange }"
                type="checkbox"
                name="paymentMethods.payAt"
              >
                <FormItem>
                  <div class="flex gap-2 items-center">
                    <FormControl>
                      <CheckboxComponent
                        :model-value="value"
                        @update:model-value="handleChange"
                        :disabled="!shouldAllowMerchantToEnablePaymentMethods.payAt"
                      />
                    </FormControl>
                    <FormLabel class="font-medium">Pay@</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormField
                v-if="props.settings.features?.enableTerminalCard"
                v-slot="{ value, handleChange }"
                type="checkbox"
                name="paymentMethods.terminalCardPayment"
              >
                <FormItem>
                  <div class="flex gap-2 items-center">
                    <FormControl>
                      <CheckboxComponent
                        :model-value="value"
                        @update:model-value="handleChange"
                        :disabled="!shouldAllowMerchantToEnablePaymentMethods.terminalCard"
                      />
                    </FormControl>
                    <FormLabel class="font-medium">Terminal Card Payment</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormField
                v-if="props.settings.features?.enableOfflinePayment"
                v-slot="{ value, handleChange }"
                type="checkbox"
                name="paymentMethods.offlineCashPayment"
              >
                <FormItem>
                  <div class="flex gap-2 items-center">
                    <FormControl>
                      <CheckboxComponent :model-value="value" @update:model-value="handleChange" />
                    </FormControl>
                    <FormLabel class="font-medium">Offline Cash Payment</FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              </FormField>
              <FormMessage v-if="errors">{{ typeof errors === 'string' ? errors : errors[0] }}</FormMessage>
            </div>
          </FormItem>
        </FormField>
      </div>
    </div>
    <SectionDivider class="my-4" />
    <div class="overflow-x-auto">
      <div class="flex mb-3">
        <Button
          v-for="control in subTabs"
          :key="control.value"
          @click="selectedTab = control.value"
          type="button"
          variant="outline"
          class="flex items-center gap-2 flex-1 min-w-[150px] max-w-[150px]"
          :class="[{ 'text-grey-light-800 bg-grey-light-50': selectedTab === control.value }, control.class]"
        >
          <div class="h-2 w-2 rounded-full bg-success-500" v-if="selectedTab === control.value" />
          {{ control.label }}
        </Button>
      </div>
    </div>
    <div class="mt-6">
      <OrderTabs.PaymentPlansTab v-if="selectedTab === 'payment-plans'" :settings="settings" :form="form" />
      <OrderTabs.BoostRewardsTab v-if="selectedTab === 'boost-rewards'" :form="form" />
    </div>

    <div class="flex justify-end gap-4 mt-4 mr-8">
      <Button class="w-auto" type="submit" data-testid="save-button" @click="onSubmit">Save</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Button } from '@/components/ui/button';
import CheckboxComponent from '@/components/ui/checkbox/CheckboxComponent.vue';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { LabelComponent } from '@/components/ui/label';
import SwitchComponent from '@/components/ui/switch/SwitchComponent.vue';
import { NetworkError } from '@/errors';
import { saveMerchant } from '@/services/merchants';
import { getPricelistById } from '@/services/priceLists';
import { getValidCampaignSettings } from '@/services/yoyo';
import { toCents, toRand } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { checkAvailablePaymentMethodsInPriceList } from '@/utils/order.utils';
import { logger } from '@sentry/vue';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import { z } from 'zod';
import type { ModelMerchantRequest, ModelYoyoResponse } from '@layup/api-sdk';
import * as OrderTabs from '../order-tabs';
import { offsetFreshchatOnScrollToBottom } from '@/utils/freshchat.utils';

const props = defineProps<{
  settings: ModelMerchantRequest;
  merchantId: string;
}>();

const emit = defineEmits(['save:merchant']);

const toast = useToast();

const isLoading = ref(false);
const selectedTab = ref<'payment-plans' | 'boost-rewards'>('payment-plans');

const subTabs: { label: string; class: string; value: 'payment-plans' | 'boost-rewards' }[] = [
  { value: 'payment-plans', label: 'Payment Plans', class: 'rounded-r-none' },
  { value: 'boost-rewards', label: 'Boost Rewards', class: 'rounded-l-none' },
];

const ordersFormSchema: any = toTypedSchema(
  z
    .object({
      paymentMethods: z
        .object({
          capitecPay: z.boolean().optional(),
          card: z.boolean().optional(),
          instantEft: z.boolean().optional(),
          debiCheck: z.boolean().optional(),
          debiCheckMinAmount: z
            .string()
            .optional()
            .refine(
              (value) =>
                form.values.paymentMethods.debiCheck && debiCheckMinAmountEnabled.value && !value ? false : true,
              { message: 'DebiCheck minimum amount is required when enabled' },
            ),
          payAt: z.boolean().optional(),
          terminalCardPayment: z.boolean().optional(),
          offlineCashPayment: z.boolean().optional(),
        })
        .refine((data) => data.card || data.instantEft || data.debiCheck || data.payAt, {
          message: 'At least one payment method must be selected',
          path: [], // Make sure error sits at root so it doesn't display 'required'
        }),
      frequency: z
        .object({
          allowMonthlyPayments: z.boolean().optional(),
          allowDefaultMinMonths: z.boolean().optional(),
          allowDefaultMaxMonths: z.boolean().optional(),
          defaultMinMonths: z
            .preprocess(
              (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
              z
                .number({ required_error: 'Minimum term is required' })
                .int()
                .min(1, { message: 'Minimum term must be at least 1 month' })
                .max(36, { message: 'Minimum term must be at most 36 months' })
                .optional(),
            )
            .superRefine((val, ctx) => {
              const allowDefaultMinMonths = form.values?.frequency?.allowDefaultMinMonths as number;

              if (allowDefaultMinMonths && (val === undefined || val === null)) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Minimum term is required when enabled',
                  path: [],
                });
              }
            }),
          defaultMaxMonths: z
            .preprocess(
              (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
              z
                .number({ required_error: 'Maximum term is required' })
                .int()
                .min(1, { message: 'Maximum term must be at least 1 month' })
                .max(36, { message: 'Maximum term must be at most 36 months' })
                .optional(),
            )
            .superRefine((val, ctx) => {
              const allowDefaultMaxMonths = form.values?.frequency?.allowDefaultMaxMonths as number;

              if (allowDefaultMaxMonths && (val === undefined || val === null)) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Maximum term is required when enabled',
                  path: [],
                });
              }
            }),
        })
        .superRefine((data, ctx) => {
          if (
            data.allowDefaultMinMonths &&
            data.allowDefaultMaxMonths &&
            data.defaultMinMonths !== undefined &&
            data.defaultMaxMonths !== undefined
          ) {
            if (data.defaultMinMonths > data.defaultMaxMonths) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Minimum term must be less than or equal to maximum term',
                path: ['defaultMinMonths'],
              });
            }
          }
        }),
      orders: z
        .object({
          allowSplitPayments: z.boolean().optional(),
          suspendOnCancel: z.boolean().optional(),
          orderExpiry: z.boolean().optional(),
          minimumOrderValue: z.number().optional().default(0),
          maximumOrderValue: z.number().optional().default(0),
          timeValue: z
            .number()
            .optional()
            .refine((value) => (form.values.orders.orderExpiry && !value ? false : true), {
              message: 'Time value is required when order expiry is enabled',
            }),
          timeUnit: z
            .string()
            .optional()
            .refine((value) => (form.values.orders.orderExpiry && !value ? false : true), {
              message: 'Time unit is required when order expiry is enabled',
            })
            .refine((value) => (value ? true : false), { message: 'Time unit is required' }),
        })
        .superRefine((data, ctx) => {
          const { minimumOrderValue, maximumOrderValue } = data;

          if (
            minimumOrderValue > 0 &&
            maximumOrderValue > 0
          ) {
            if (minimumOrderValue >= maximumOrderValue) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Minimum order value must be less than maximum order value',
                path: ['minimumOrderValue'],
              });
            }
          }
        }),
      depositSettings: z.object({
        allowDefaultDepositType: z.boolean().optional(),
        defaultDepositType: z
          .string()
          .optional()
          .refine((value) => (form.values.depositSettings.allowDefaultDepositType && !value ? false : true), {
            message: 'Deposit type needs to be set',
          }),
        defaultDepositTypePercentage: z
          .string()
          .optional()
          .refine(
            (value) => (form.values.depositSettings.defaultDepositType === 'PERCENTAGE' && !value ? false : true),
            {
              message: 'Percentage is required',
            },
          ),
        defaultDepositTypeFlatFee: z
          .string()
          .optional()
          .refine(
            (value) => {
              if (form.values.depositSettings.defaultDepositType === 'FLAT' && !value) return false;
              if (value) {
                return /^(?:[0-9]|[1-9][0-9]{1,5})$/.test(value);
              }
              return true;
            },
            { message: 'Flat fee must be a valid number' },
          ),

        allowDefaultOverride: z.boolean().optional(),
      }),
      boostSettings: z
        .object({
          allowVoucher: z.boolean().optional(),
          minSavingGoal: z
            .number()
            .optional()
            .superRefine((value, ctx) => {
              if (form.values.boostSettings.allowVoucher && !value) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Minimum saving goal is required when voucher is enabled',
                });
              }
            }),
          maxSavingGoal: z
            .number()
            .optional()
            .superRefine((value, ctx) => {
              if (form.values.boostSettings.allowVoucher && !value) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Maximum saving goal is required when voucher is enabled',
                });
              }
            }),
          rewardType: z
            .string({ required_error: 'Reward type is required' })
            .optional()
            .superRefine((value, ctx) => {
              if (form.values.boostSettings.allowVoucher && !value) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Reward type is required when voucher is enabled',
                });
              }
            }),
          rewardPercentage: z
            .string()
            .optional()
            .superRefine((value, ctx) => {
              if (
                form.values.boostSettings.allowVoucher &&
                form.values.boostSettings.rewardType === 'PERCENTAGE' &&
                !value
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Percentage is required when voucher is enabled',
                });
              }
            }),
          rewardFlatFee: z
            .number()
            .optional()
            .superRefine((value, ctx) => {
              if (form.values.boostSettings.allowVoucher && form.values.boostSettings.rewardType === 'FLAT' && !value) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Flat fee is required when voucher is enabled',
                });
              }

              if (value) {
                const isValid = /^(?:[0-9]|[1-9][0-9]{1,5})$/.test(value.toString());
                if (!isValid) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Flat fee must be a valid number',
                  });
                }
              }
            }),
          rewardProvider: z
            .string()
            .optional()
            .superRefine((value, ctx) => {
              if (form.values.boostSettings.allowVoucher && !value) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Reward provider is required when voucher is enabled',
                });
              }
            }),
        })
        .superRefine((data, ctx) => {
          const { allowVoucher, minSavingGoal, maxSavingGoal } = data;

          if (allowVoucher && minSavingGoal !== undefined && maxSavingGoal !== undefined) {
            if (minSavingGoal >= maxSavingGoal) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Minimum saving goal must be less than maximum saving goal',
                path: ['minSavingGoal'],
              });
            }
          }
        }),
      cancellationFees: z.object({
        deposit: z.boolean().optional(),
        percentage: z.boolean().optional(),
        percentageType: z.string().optional(),
        percentageValue: z
          .string()
          .optional()
          .refine((value) => (form.values.cancellationFees.percentage && !value ? false : true), {
            message: 'Percentage value is required when percentage is enabled',
          }),
        flatFee: z.boolean().optional(),
        flatFeeValue: z
          .number()
          .optional()
          .refine(
            (value) =>
              !form.values.cancellationFees.flatFee ||
              (value !== undefined && Number(value) >= 1 && Number(value) <= 10000000),
            {
              message: 'Flat fee value is required and must be between R1 and R100,000 when flat fee is enabled',
            },
          ),
      }),
      autoCancellations: z.object({
        autoCancellation: z.boolean().optional(),
        missedPayments: z.boolean().optional(),
        numberOfMissedPayments: z
          .string()
          .optional()
          .refine((value) => (form.values.autoCancellations.missedPayments && !value ? false : true), {
            message: 'Required when missed payments is enabled',
          }),
        gracePeriod: z.boolean().optional(),
        gracePeriodValue: z
          .string()
          .optional()
          .refine((value) => (form.values.autoCancellations.gracePeriod && (!value || value === '0') ? false : true), {
            message: 'Grace period value is required when grace period is enabled',
          }),
        finalPaymentMissed: z.boolean().optional(),
        timeBased: z.boolean().optional(),
        percentageComplete: z.number().min(0).max(100).optional(),
        daysBeforeFinalPayment: z.number().min(0).max(120).optional(),
      }),
    })
    .superRefine((data, ctx) => {
      if (!data.autoCancellations.autoCancellation) return;

      const { missedPayments, numberOfMissedPayments, finalPaymentMissed, timeBased, percentageComplete } =
        data.autoCancellations;

      // Check if at least one option is selected (regardless of value validity)
      const isAnySelected = missedPayments || finalPaymentMissed || timeBased;

      if (!isAnySelected) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'At least one cancellation condition must be set: Missed Payments, Final Payment Missed, or Time-Based.',
          path: ['autoCancellations.autoCancellation'],
        });
      }

      // Individual validation (only if selected)

      if (missedPayments && !numberOfMissedPayments) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Number of missed payments is required when Missed Payments is enabled.',
          path: ['autoCancellations.numberOfMissedPayments'],
        });
      }

      if (timeBased) {
        if (!percentageComplete) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Percentage Complete is required when Time-Based is enabled.',
            path: ['autoCancellations.percentageComplete'],
          });
        }
      }
    }),
);

const form = useForm({
  validationSchema: ordersFormSchema,
});

const debiCheckMinAmountEnabled = ref(false);
const priceList = ref({} as any);
const isDebiCheckSelected = computed(() => form?.values?.paymentMethods?.debiCheck); // For payment methods
const shouldAllowMerchantToEnablePaymentMethods = computed(() => {
  return checkAvailablePaymentMethodsInPriceList(priceList.value.products);
});

// Set form values when settings are loaded or changed
watch(
  () => props.settings,
  async (newSettings) => {
    if (!newSettings) {
      isLoading.value = false;
      return;
    }

    isLoading.value = true;

    form.setValues({
      paymentMethods: {
        capitecPay: newSettings.paymentPlans?.paymentMethods?.capitecPay || false,
        card: newSettings.paymentPlans?.paymentMethods?.creditOrDebitCard || false,
        instantEft: newSettings.paymentPlans?.paymentMethods?.eft || false,
        debiCheck: newSettings.paymentPlans?.paymentMethods?.debiCheck || false,
        debiCheckMinAmount: ((newSettings.paymentPlans?.debiCheckMinimum?.value || 0) / 100).toString(),
        payAt: newSettings.paymentPlans?.paymentMethods?.payAt || false,
        terminalCardPayment: newSettings.paymentPlans?.paymentMethods?.terminalCard || false,
        offlineCashPayment: newSettings.paymentPlans?.paymentMethods?.offline || false,
      },
    });

    if (selectedTab.value === 'payment-plans') {
      setPaymentPlansValues(newSettings);
    }

    if (selectedTab.value === 'boost-rewards') {
      setBoostRewardsValues(newSettings);
    }

    // Time is stored in minutes even if hours or days are chosen so setting default value to minutes
    form.setFieldValue('orders.timeUnit', 'minutes');

    // Check if pricelistId is set so that payment methods can be enabled/disabled
    if (newSettings.pricelistId) {
      const response = await getPricelistById(newSettings.pricelistId);
      priceList.value = response;
    }

    // Silent validation won't display errors till user submits or touches a field
    form.validate({ mode: 'silent' });
    isLoading.value = false;
  },
  { immediate: true, deep: true },
);

// Watch selectedTab to reset form values when switching between tabs because values are lost when removed with v-if
watch(
  () => selectedTab.value,
  (tab) => {
    if (!props.settings) return;

    if (tab === 'payment-plans') {
      setPaymentPlansValues(props.settings);
    }

    if (tab === 'boost-rewards') {
      setBoostRewardsValues(props.settings);
    }
  },
);

/**
 * When form fields are hidden and later made visible again, they lose their state. To maintain
 * consistency, we listen to controlling fields and reset dependent field values when they are re-enabled.
 */
watch([() => form.values.paymentMethods.debiCheck, () => debiCheckMinAmountEnabled.value], ([debiCheckState]) => {
  if (debiCheckState) {
    form.setFieldValue(
      'paymentMethods.debiCheckMinAmount',
      ((props.settings.paymentPlans?.debiCheckMinimum?.value || 0) / 100).toString(),
    );
  }
});

watch(
  [() => form.values?.frequency?.allowDefaultMinMonths, () => form.values?.frequency?.allowDefaultMaxMonths],
  ([minState, maxState]) => {
    const paymentPlans = props.settings.paymentPlans;
    if (minState) {
      form.setFieldValue('frequency.defaultMinMonths', paymentPlans?.minMonths?.value?.toString());
    }
    if (maxState) {
      form.setFieldValue('frequency.defaultMaxMonths', paymentPlans?.maxMonths?.value?.toString());
    }
  },
);

watch(
  () => form.values?.orders?.orderExpiry,
  (state) => {
    if (state) {
      form.setFieldValue('orders.timeValue', props.settings.paymentPlans?.orderExpiry?.value);
      form.setFieldValue('orders.timeUnit', 'minutes');
    }
  },
);

watch(
  () => form.values?.boostSettings?.allowVoucher,
  (state) => {
    if (state) {
      const voucherSettings = props.settings.voucherSettings;
      form.setFieldValue('boostSettings.minSavingGoal', voucherSettings?.minimumSavingGoalInCents || 0);
      form.setFieldValue('boostSettings.maxSavingGoal', voucherSettings?.maximumSavingGoalInCents || 0);
      form.setFieldValue('boostSettings.rewardType', props.settings.voucherSettings?.rewardType);

      if (voucherSettings?.active) {
        if (voucherSettings?.rewardType === 'PERCENTAGE') {
          form.setFieldValue('boostSettings.rewardPercentage', voucherSettings?.rewardPercentage?.toString());
        }

        if (voucherSettings?.rewardType === 'FLAT') {
          form.setFieldValue('boostSettings.flat', voucherSettings?.rewardFlatAmountInCents || 0);
        }
      }
    }
  },
);

watch(
  () => form.values?.boostSettings?.rewardProvider,
  (state) => {
    const voucherSettings = props.settings.voucherSettings;
    if (state === 'YOYO') {
      if (voucherSettings?.active) {
        const yoyoProvider = voucherSettings?.voucherProviders?.find((p) => p.name === 'YOYO');
        form.setFieldValue('boostSettings.campaignId', yoyoProvider?.yoyoCampaignId || '');
      } else {
        form.setFieldValue('boostSettings.campaignId', '');
      }
    } else {
      // Clear YOYO-specific campaignId when switching away from YOYO or when provider is unset.
      form.setFieldValue('boostSettings.campaignId', '');
    }
  },
);

const validateOrderExpire = (timeUnit: 'minutes' | 'hours' | 'days', timeValue: number): boolean => {
  if (timeUnit === 'minutes') {
    if (timeValue < 0 || timeValue > 86400) {
      form.setFieldError('orders.timeValue', 'Time value must be between 0 and 86400 minutes (60 days)');
      return false;
    }
  }

  if (timeUnit === 'hours') {
    if (timeValue < 0 || timeValue > 1440) {
      form.setFieldError('orders.timeValue', 'Time value must be between 0 and 1440 hours (60 days)');
      return false;
    }
  }

  if (timeUnit === 'days') {
    if (timeValue < 0 || timeValue > 60) {
      form.setFieldError('orders.timeValue', 'Time value must be between 0 and 60 days');
      return false;
    }
  }

  return true;
};

const validateCampaignId = async (campaign: ModelYoyoResponse): Promise<boolean> => {
  try {
    if (campaign === null || campaign === undefined) {
      form.setFieldError('boostSettings.campaignId', 'Campaign ID is invalid or campaign is not active');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error validating campaign ID', error);
    form.setFieldError('boostSettings.campaignId', 'Campaign ID is invalid or campaign is not active');
    return false;
  }
};

const validateCampaignMaxSettings = async (campaign: ModelYoyoResponse): Promise<boolean> => {
  try {
    const merchantMaxValueSetting = form.values.boostSettings.maxSavingGoal;

    if (campaign) {
      const maxValue = campaign?.giftcardCampaign?.maxValueAllowedToIssue;
      if (maxValue && merchantMaxValueSetting && maxValue !== merchantMaxValueSetting) {
        form.setFieldError(
          'boostSettings.maxSavingGoal',
          `Maximum saving goal must be R ${toRand(maxValue)} to be compatible with the campaign`,
        );
        return false;
      }
      return true;
    }

    return true;
  } catch (error) {
    logger.error('Error validating campaign settings', { error });
    form.setFieldError('boostSettings.campaignId', 'Unable to validate campaign settings at this time');
    return false;
  }
};
const validateCampaignMinSettings = async (campaign: ModelYoyoResponse): Promise<boolean> => {
  try {
    const merchantMinValueSetting = form.values.boostSettings.minSavingGoal;

    if (campaign) {
      const minValue = campaign?.giftcardCampaign?.minValueAllowedToIssue;

      if (minValue && merchantMinValueSetting && minValue !== merchantMinValueSetting) {
        form.setFieldError(
          'boostSettings.minSavingGoal',
          `Minimum saving goal must be R ${toRand(minValue)} to be compatible with the campaign`,
        );
        return false;
      }
      return true;
    }

    return true;
  } catch (error) {
    logger.error('Error validating campaign settings', { error });
    form.setFieldError('boostSettings.campaignId', 'Unable to validate campaign settings at this time');
    return false;
  }
};
function setPaymentPlansValues(settings: ModelMerchantRequest) {
  const minimumOrderValueInCents = settings.minimumOrderValueInCents ?? 0;
  const maximumOrderValueInCents = settings.maximumOrderValueInCents ?? 0;

  let formValues: any = {
    frequency: {
      allowMonthlyPayments: settings.paymentPlans?.monthlyPayments || false,
      allowDefaultMinMonths: settings.paymentPlans?.minMonths?.enabled || false,
      allowDefaultMaxMonths: settings.paymentPlans?.maxMonths?.enabled || false,
      defaultMinMonths:
        settings.paymentPlans?.minMonths?.value !== undefined
          ? String(settings.paymentPlans?.minMonths?.value)
          : undefined,
      defaultMaxMonths:
        settings.paymentPlans?.maxMonths?.value !== undefined
          ? String(settings.paymentPlans?.maxMonths?.value)
          : undefined,
    },
    orders: {
      allowSplitPayments: settings.paymentPlans?.splitPayments || false,
      suspendOnCancel: settings.paymentPlans?.suspendOnCancel || false,
      orderExpiry: settings.paymentPlans?.orderExpiry?.enabled || false,
      timeValue: settings.paymentPlans?.orderExpiry?.value || undefined,
      minimumOrderValue: minimumOrderValueInCents,
      maximumOrderValue: maximumOrderValueInCents,
    },
    depositSettings: {
      allowDefaultDepositType: settings.paymentPlans?.defaultDepositType?.enabled || false,
      defaultDepositType: settings.paymentPlans?.defaultDepositType?.depositType,
      defaultDepositTypePercentage: settings.paymentPlans?.defaultDepositType?.percentage?.toString(),
      defaultDepositTypeFlatFee: ((settings.paymentPlans?.defaultDepositType?.flat || 0) / 100).toString(),
      allowDefaultOverride: settings.paymentPlans?.editPaymentPlanSettings || false,
    },
  };

  const cancellationTerms = settings?.cancellationTerms?.definition
    ? settings.cancellationTerms.definition[0]
    : undefined;

  if (cancellationTerms) {
    // Cancellation fees
    formValues = {
      ...formValues,
      cancellationFees: {
        ...formValues.cancellationFees,
        deposit: !!cancellationTerms.policy?.definition?.deposit,
        percentage: (cancellationTerms.policy?.definition?.percentage || 0) > 0, // There is no enabled/disabled value for percentage so I'm using the value to check the box or not
        percentageType: cancellationTerms.policy?.definition?.percentageShouldUseTotalPaid
          ? 'totalPaidValue'
          : 'totalOrderValue',
        percentageValue: cancellationTerms.policy?.definition?.percentage?.toString() || '0',
        flatFee: (cancellationTerms.policy?.definition?.flatFee || 0) > 0,
        flatFeeValue:
          cancellationTerms.policy?.definition?.flatFee && cancellationTerms.policy?.definition?.flatFee >= 100
            ? cancellationTerms.policy?.definition?.flatFee
            : 0,
      },
    };

    // Auto cancellations
    if (cancellationTerms.type && cancellationTerms.type.definition) {
      formValues = {
        ...formValues,
        autoCancellations: {
          autoCancellation: settings.cancellationTerms?.active || false,
          missedPayments: cancellationTerms?.type?.definition?.missed
            ? cancellationTerms?.type?.definition?.missed > 0
            : false,
          numberOfMissedPayments: cancellationTerms?.type?.definition?.missed?.toString() || '0',
          gracePeriod: cancellationTerms.gracePeriod ? cancellationTerms.gracePeriod > 0 : false,
          gracePeriodValue: cancellationTerms.gracePeriod?.toString() || '0',
          finalPaymentMissed: cancellationTerms?.type?.finalPaymentMissed || false,
          // Final payment just sets type.description.percentage to 100 and missed to 0
          // So we can just check if finalPaymentMissed is true to determine if its enabled or timeBased
          timeBased:
            !cancellationTerms?.type?.finalPaymentMissed && cancellationTerms?.type.definition.percentage
              ? cancellationTerms?.type.definition.percentage > 0
              : false,
          percentageComplete: cancellationTerms?.type?.definition?.percentage || 0,
          // End is stored as a negative number so we need to convert it to a positive number
          daysBeforeFinalPayment: cancellationTerms.end ? Math.abs(cancellationTerms.end) : 0,
        },
      };
    }
  }

  form.setValues(formValues);

  // Setting refs not part of the form schema
  debiCheckMinAmountEnabled.value = settings?.paymentPlans?.debiCheckMinimum?.enabled || false;
}

function setBoostRewardsValues(settings: ModelMerchantRequest) {
  form.setValues({
    boostSettings: {
      allowVoucher: settings.voucherSettings?.active || false,
      minSavingGoal: settings.voucherSettings?.minimumSavingGoalInCents || 0,
      maxSavingGoal: settings.voucherSettings?.maximumSavingGoalInCents || 0,
      rewardType: settings.voucherSettings?.rewardType,
      rewardPercentage: settings.voucherSettings?.rewardPercentage?.toString(),
      rewardFlatFee: settings.voucherSettings?.rewardFlatAmountInCents || 0,
    },
  });

  // Checking which voucher setting input to set. Percentage or Flat fee and setting provider
  if (settings.voucherSettings?.active) {
    if (settings.voucherSettings?.rewardType === 'PERCENTAGE') {
      form.setFieldValue('boostSettings.rewardPercentage', settings.voucherSettings.rewardPercentage?.toString());
    }

    if (settings.voucherSettings?.rewardType === 'FLAT') {
      form.setFieldValue('boostSettings.rewardFlatFee', settings.voucherSettings?.rewardFlatAmountInCents || 0);
    }
    const voucherProviders = settings.voucherSettings?.voucherProviders;
    if (Array.isArray(voucherProviders) && voucherProviders.length > 1) {
      const providers = voucherProviders.find((p) => p.active === true);
      form.setFieldValue('boostSettings.rewardProvider', providers?.name || '');
      form.setFieldValue('boostSettings.campaignId', providers?.yoyoCampaignId || '');
    } else {
      form.setFieldValue('boostSettings.rewardProvider', settings.voucherSettings?.voucherProviders?.[0]?.name || '');
      form.setFieldValue(
        'boostSettings.campaignId',
        settings.voucherSettings?.voucherProviders?.[0]?.yoyoCampaignId || '',
      );
    }
  }
}

const setCancellationSettings = (settings: ModelMerchantRequest) => {
  const cancellationFees = form.values.cancellationFees;
  const autoCancellations = form.values.autoCancellations;
  const definition = settings.cancellationTerms?.definition?.[0];

  if (!definition?.policy || !definition?.type) {
    return settings;
  }

  // Count how many fee types are set (non-zero or true)
  const feeTypesSet = [cancellationFees.deposit, cancellationFees.percentage, cancellationFees.flatFee].filter(
    Boolean,
  ).length;

  // If more than one fee type is set, use 'Combination'
  if (feeTypesSet > 1) {
    definition.policy.name = 'Combination';
  } else if (cancellationFees.deposit) {
    definition.policy.name = 'Deposit';
  } else if (cancellationFees.percentage) {
    definition.policy.name = 'Percentage';
  } else if (cancellationFees.flatFee) {
    definition.policy.name = 'FlatFee';
  } else {
    // FlatFee 0 is a valid cancellation policy.
    definition.policy.name = 'FlatFee';
  }

  if (autoCancellations.missedPayments && Number(autoCancellations.numberOfMissedPayments) > 0) {
    definition.type.name = 'InstalmentBased';
  } else if (autoCancellations.timeBased) {
    definition.type.name = 'TimeBased';
  }

  if (autoCancellations.finalPaymentMissed && definition.type.definition) {
    definition.type.definition.percentage = 100;
    definition.type.definition.missed = 0;
  }

  return settings;
};

const onSubmit = async () => {
  // Checking if the min and max dates are set to 0 and if so setting them to 1
  // Need to do this before form validation because if the values are 0, the validation will fail
  if (selectedTab.value === 'payment-plans') {
    if (form.values?.frequency?.allowDefaultMinMonths === false && form.values?.frequency?.defaultMinMonths === '0') {
      form.setFieldValue('frequency.defaultMinMonths', '1');
    }

    if (form.values.frequency.allowDefaultMaxMonths === false && form.values.frequency.defaultMaxMonths === '0') {
      form.setFieldValue('frequency.defaultMaxMonths', '1');
    }
  }

  // Setting the values of the tab that isn't visible because when the section is removed from the DOM all the values are set to undefined
  if (selectedTab.value === 'payment-plans') {
    setBoostRewardsValues(props.settings);
  } else if (selectedTab.value === 'boost-rewards') {
    setPaymentPlansValues(props.settings);
  }
  console.log('BEFORE validate');
  console.log('orders', form.values.orders);
  console.log('boostSettings', form.values.boostSettings);
  const { values, errors, valid } = await form.validate();

  if (form.values?.boostSettings?.campaignId) {
    try {
      const campaignSettings = await getValidCampaignSettings(form.values.boostSettings.campaignId);
      const isValid = await validateCampaignId(campaignSettings);
      if (!isValid) {
        form.setFieldError('boostSettings.campaignId', 'Invalid campaign ID');
        return;
      } else {
        const isCampaignMaxSettingsValid = await validateCampaignMaxSettings(campaignSettings);

        const isCampaignMinSettingsValid = await validateCampaignMinSettings(campaignSettings);

        if (!isCampaignMaxSettingsValid) {
          return;
        }

        if (!isCampaignMinSettingsValid) {
          return;
        }
      }
    } catch (error) {
      form.setFieldError('boostSettings.campaignId', 'Invalid campaign ID');
      return;
    }
  }

  // Need to manually check errors because for some reason vee-validate doesn't set errors on the form when submitted even if valid is false
  if (Object.keys(errors).length > 0) {
    Object.entries(errors).forEach(([key, value]) => {
      form.setFieldError(key as keyof typeof errors, value);
    });

    return;
  }

  if (valid) {
    isLoading.value = true;
    // Check that order expiry time value doesn't exceed 60 days for minutes/hours/days
    if (values?.orders.orderExpiry) {
      const timeValue = values?.orders.timeValue;
      const timeUnit = values?.orders.timeUnit;

      if (!validateOrderExpire(timeUnit, timeValue)) {
        isLoading.value = false;
        return;
      }

      // Convert to minutes for storage
      if (timeUnit === 'hours') {
        values.orders.timeValue = timeValue * 60;
      } else if (timeUnit === 'days') {
        values.orders.timeValue = timeValue * 1440;
      }
    }

    try {
      // isLoading.value = true;
      if (props.merchantId === '') {
        toast.error('Merchant ID is not set. Please try reloading the page', { position: 'top' });
        throw new Error('Merchant ID is required.');
      }

      const minimumOrderValueInCents = Number(values?.orders?.minimumOrderValue) || 0;
      const maximumOrderValueInCents = Number(values?.orders?.maximumOrderValue) || 0;

      const settings: ModelMerchantRequest = {
        ...props.settings,
        minimumOrderValueInCents,
        maximumOrderValueInCents,
        paymentPlans: {
          ...props.settings.paymentPlans,
          paymentMethods: {
            capitecPay: values?.paymentMethods.capitecPay || false,
            creditOrDebitCard: values?.paymentMethods.card || false,
            eft: values?.paymentMethods.instantEft || false,
            debiCheck: values?.paymentMethods.debiCheck || false,
            payAt: values?.paymentMethods.payAt || false,
            terminalCard: values?.paymentMethods.terminalCardPayment || false,
            offline: values?.paymentMethods.offlineCashPayment || false,
          },
          debiCheckMinimum: {
            enabled: debiCheckMinAmountEnabled.value || false,
            value: toCents(Number(values?.paymentMethods.debiCheckMinAmount) || 0),
          },
          monthlyPayments: values?.frequency.allowMonthlyPayments || false,
          minMonths: {
            enabled: values?.frequency.allowDefaultMinMonths || false,
            value: Number(values?.frequency.defaultMinMonths) || undefined,
          },
          maxMonths: {
            enabled: values?.frequency.allowDefaultMaxMonths || false,
            value: Number(values?.frequency.defaultMaxMonths) || undefined,
          },
          splitPayments: values?.orders?.allowSplitPayments || false,
          suspendOnCancel: values?.orders?.suspendOnCancel || false,
          orderExpiry: {
            enabled: values?.orders?.orderExpiry || false,
            value: values?.orders?.timeValue || 0,
          },
          defaultDepositType: {
            enabled: values?.depositSettings?.allowDefaultDepositType || false,
            depositType: values?.depositSettings?.defaultDepositType || undefined,
            percentage: Number(values?.depositSettings?.defaultDepositTypePercentage || 0),
            flat: toCents(Number(values?.depositSettings?.defaultDepositTypeFlatFee || 0)),
          },
          editPaymentPlanSettings: values?.depositSettings?.allowDefaultOverride || false,
        },
        voucherSettings: {
          ...props.settings.voucherSettings,
          active: values?.boostSettings?.allowVoucher || false,
          minimumSavingGoalInCents: Number(values?.boostSettings?.minSavingGoal) || 0,
          maximumSavingGoalInCents: Number(values?.boostSettings?.maxSavingGoal) || 0,
          rewardType: values?.boostSettings?.rewardType || undefined,
          rewardPercentage: Number(values?.boostSettings?.rewardPercentage || 0),
          rewardFlatAmountInCents: Number(values?.boostSettings?.rewardFlatFee || 0),
          ...(values?.boostSettings?.allowVoucher && {
            voucherProviders: [
              ...(form.values.boostSettings.rewardProvider === 'SHOPIFY'
                ? [
                    {
                      name: 'SHOPIFY',
                      apiKey:
                        props.settings.voucherSettings?.voucherProviders?.find((p) => p.name === 'SHOPIFY')?.apiKey ||
                        '',
                      apiUrl:
                        props.settings.voucherSettings?.voucherProviders?.find((p) => p.name === 'SHOPIFY')?.apiUrl ||
                        '',
                      yoyoCampaignId: '',
                      active: form.values.boostSettings.rewardProvider === 'SHOPIFY' ? true : false,
                    },
                  ]
                : []),
              ...(form.values.boostSettings.rewardProvider === 'YOYO'
                ? [
                    {
                      name: 'YOYO',
                      apiKey: '',
                      apiUrl: '',
                      yoyoCampaignId: form.values.boostSettings.campaignId || '',
                      active: form.values.boostSettings.rewardProvider === 'YOYO' ? true : false,
                    },
                  ]
                : []),
            ],
          }),
        },
        cancellationTerms: {
          active: values?.autoCancellations?.autoCancellation || false,
          definition: [
            {
              start: 0,
              end:
                (form.values.autoCancellations.timeBased
                  ? Number(form.values.autoCancellations.daysBeforeFinalPayment || 0)
                  : 0) * -1, // End is stored as a negative number
              gracePeriod: Number(values?.autoCancellations.gracePeriodValue),
              policy: {
                definition: {
                  flatFee: Number(values?.cancellationFees.flatFeeValue || 0),
                  percentage: Number(values?.cancellationFees.percentageValue),
                  percentageShouldUseTotalPaid: values?.cancellationFees.percentageType === 'totalPaidValue',
                },
              },
              type: {
                definition: {
                  missed: Number(values?.autoCancellations.numberOfMissedPayments),
                  percentage: Number(values?.autoCancellations.percentageComplete),
                },
                finalPaymentMissed: values?.autoCancellations.finalPaymentMissed,
                name: 'TimeBased',
              },
            },
          ],
        },
      };

      setCancellationSettings(settings);
      const response = await saveMerchant({
        id: props.merchantId,
        modelMerchantRequest: settings,
      });

      emit('save:merchant', response);

      addMetric('settings-page-orders', { status: 'success', channel: 'merchant', method: 'saveMerchant' });
    } catch (error) {
      if (error instanceof NetworkError) {
        createWarning(error);
        addMetric('settings-page-organization', {
          status: 'network_error',
          channel: 'merchant',
          method: 'saveMerchant',
        });
      } else {
        createException(error);
        addMetric('settings-page-organization', { status: 'error', channel: 'merchant', method: 'saveMerchant' });
      }
    }
  }
};

offsetFreshchatOnScrollToBottom();
</script>
